import React from "react";
import { motion } from "framer-motion";
import { skills } from "../portfolio";
import { blurIn, staggerContainer, popIn } from "../utils/motion";
import SpotlightCard from "./SpotlightCard";
import ScrambleText from "./ScrambleText";
import { playHover } from "../utils/soundEngine";

function TechMarquee() {
  const allSkills = skills.data.flatMap((c) => c.softwareSkills);
  const doubled = [...allSkills, ...allSkills];

  return (
    <div className="relative mb-16 overflow-hidden py-4 space-y-3.5 select-none">
      {/* Fade edges */}
      <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-bg to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-bg to-transparent z-10 pointer-events-none" />

      {/* Row 1 — forward */}
      <div className="flex w-max gap-4 animate-marquee pause-on-hover will-change-transform">
        {doubled.map((skill, i) => (
          <div
            key={`fwd-${skill.skillName}-${i}`}
            onMouseEnter={() => playHover()}
            className="group flex items-center gap-2 shrink-0 px-3.5 py-1.5 rounded-full border border-surface/[0.06] bg-surface/[0.02] hover:bg-surface/[0.06] hover:border-surface/[0.14] transition-all duration-300 cursor-default"
          >
            <span
              className="iconify text-base grayscale group-hover:grayscale-0 opacity-60 group-hover:opacity-100 transition-all duration-300"
              data-icon={skill.fontAwesomeClassname}
              style={{ color: skill.style?.color || "#a0a0a0" }}
            />
            <span className="text-xs font-mono text-fg-muted group-hover:text-fg transition-colors duration-300">
              {skill.skillName}
            </span>
          </div>
        ))}
      </div>

      {/* Row 2 — reverse */}
      <div className="flex w-max gap-4 animate-marquee-reverse pause-on-hover will-change-transform">
        {doubled.map((skill, i) => (
          <div
            key={`rev-${skill.skillName}-${i}`}
            onMouseEnter={() => playHover()}
            className="group flex items-center gap-2 shrink-0 px-3.5 py-1.5 rounded-full border border-surface/[0.06] bg-surface/[0.02] hover:bg-surface/[0.06] hover:border-surface/[0.14] transition-all duration-300 cursor-default"
          >
            <span
              className="iconify text-base grayscale group-hover:grayscale-0 opacity-60 group-hover:opacity-100 transition-all duration-300"
              data-icon={skill.fontAwesomeClassname}
              style={{ color: skill.style?.color || "#a0a0a0" }}
            />
            <span className="text-xs font-mono text-fg-muted group-hover:text-fg transition-colors duration-300">
              {skill.skillName}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SkillCategory({ category, index }) {
  const isAi = category.title.toLowerCase().includes("ai");

  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, scale: 0.94, y: 24 },
        show: {
          opacity: 1,
          scale: 1,
          y: 0,
          transition: {
            type: "spring",
            stiffness: 100,
            damping: 20,
            delay: index * 0.1,
          },
        },
      }}
      className="h-full"
    >
      <SpotlightCard
        className="h-full relative p-7 sm:p-8 rounded-2xl border border-surface/[0.08] bg-surface/[0.015] hover:border-surface/[0.16] hover:bg-surface/[0.025] transition-all duration-400 ease-out-expo shadow-[0_4px_24px_rgba(0,0,0,0.03)] flex flex-col justify-between group"
        spotlightColor="rgba(var(--color-surface), 0.06)"
      >
        {/* Specular corner glow */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-surface/[0.03] rounded-full blur-2xl pointer-events-none group-hover:bg-surface/[0.06] transition-colors duration-500" />

        <div>
          {/* Domain Badge */}
          <div className="mb-4">
            <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-fg-faint">
              {isAi
                ? "DOMAIN // AGENTIC AI & LLMS"
                : "DOMAIN // SYSTEM ARCHITECTURE"}
            </span>
          </div>

          {/* Category Title */}
          <h3 className="font-display text-xl font-bold text-fg mb-6 tracking-tight">
            {category.title}
          </h3>

          {/* Architectural Principles */}
          <ul className="space-y-3.5 mb-8">
            {category.skills.map((skill, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-sm text-fg-muted leading-relaxed"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-fg-ghost mt-2 shrink-0 group-hover:bg-fg-faint transition-colors" />
                <span>{skill}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Tech Chips Grid */}
        <div className="pt-6 border-t border-surface/[0.06]">
          <motion.div
            variants={staggerContainer(0.03)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="flex flex-wrap gap-2"
          >
            {category.softwareSkills.map((skill) => (
              <motion.div
                key={skill.skillName}
                variants={popIn()}
                onMouseEnter={() => playHover()}
                className="group/chip flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface/[0.03] border border-surface/[0.05] hover:border-surface/[0.14] hover:bg-surface/[0.06] transition-all duration-300"
              >
                <span
                  className="iconify text-sm grayscale group-hover/chip:grayscale-0 opacity-60 group-hover/chip:opacity-100 transition-all duration-300"
                  data-icon={skill.fontAwesomeClassname}
                  style={{ color: skill.style?.color || "#a0a0a0" }}
                />
                <span className="text-[11px] font-mono text-fg-faint group-hover/chip:text-fg transition-colors duration-300">
                  {skill.skillName}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </SpotlightCard>
    </motion.div>
  );
}

function Skills() {
  return (
    <motion.div
      variants={staggerContainer()}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.1 }}
    >
      <motion.div variants={blurIn("up")} className="mb-12">
        <span className="section-label">Expertise</span>
        <h2 className="section-title">
          <ScrambleText text="Skills & Capabilities" hover={true} />
        </h2>
        <p className="section-subtitle">
          Refined through years of building production-grade applications at
          scale
        </p>
      </motion.div>

      <TechMarquee />

      <div className="grid md:grid-cols-2 gap-6">
        {skills.data.map((category, i) => (
          <SkillCategory key={category.title} category={category} index={i} />
        ))}
      </div>
    </motion.div>
  );
}

export default Skills;
